import { SelectFields } from '@repo/toolkit/types'

import { RoleEnum } from '../../enums/users/role.enum'
import { Enrollment } from '../../models/enrollment/enrollment'
import { DeepPartial, FieldsSelector } from '../../utils'

// ------------------------- ENROLLMENT BASE -------------------------
export const enrollmentBaseFields = [
  'id',
  'propositionType',
  'mainProposition.mainTrip.hasTransport',
  // a One to One Individuel family only fills a voyage individuel when LEC picks the youth up
  'otoIndividualProposition.transferMethod'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentBaseType = SelectFields<Enrollment, typeof enrollmentBaseFields>

// ------------------------- ENROLLMENT CHILD INFO STATUS -------------------------
export const enrollmentChildInfoStatusFields = [
  'id',
  'childInfoStatus'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentChildInfoStatusType = SelectFields<
  Enrollment,
  typeof enrollmentChildInfoStatusFields
>

// ------------------------- ENROLLMENT EXTENDED HEALTH -------------------------
export const enrollmentExtendedHealthFields = [
  'id',
  'medicalLevel',
  'medicalStatus',
  'medicalInformations',
  'additionalMedicalInformations',
  'declarationSignedAt',
  'medicalInformationsEn',
  'additionalMedicalInformationsEn',
  'medicalAttestationSending.acceptedAt',
  'medicalAttestationSending.signed',
  'medicalAttestationSending.type'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentExtendedHealthType = SelectFields<
  Enrollment,
  typeof enrollmentExtendedHealthFields
>

// ------------------------- ENROLLMENT MAIN MEDICAL INFORMATIONS LOCK -------------------------
// the backend decides whether LEC may write the main health field, from origin, status and
// confirmedAt, which are selected along with it
export const enrollmentMainMedicalInformationsLockFields = [
  'id',
  'origin',
  'status',
  'confirmedAt',
  'canEditMainMedicalInformations'
] as const satisfies FieldsSelector<Enrollment>

// ------------------------- ENROLLMENT CHILD BASE INFO -------------------------
export const enrollmentChildNameFields = [
  'id',
  'childFirstName',
  'childLastName'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentChildNameType = SelectFields<Enrollment, typeof enrollmentChildNameFields>

// ------------------------- ENROLLMENT PROPOSITION DETAILS -------------------------
export const enrollmentPropositionDetailsFields = [
  'id',
  'childBirthDate',
  'status',
  'country'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentPropositionDetailsType = SelectFields<
  Enrollment,
  typeof enrollmentPropositionDetailsFields
>

// ------------------------- ENROLLMENT EMAIL -------------------------
export const enrollmentEmailFields = [
  'id',
  'parent1Email',
  'parent2Email',
  'contactPriority',
  'confirmedAt',
  'status',
  'mainProposition.insurances.id',
  'mainProposition.insurances.insurance.id',
  'mainProposition.insurances.insurance.name'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentEmailType = SelectFields<Enrollment, typeof enrollmentEmailFields>
// ------------------------- ENROLLMENT CHILD PICKUP -------------------------
export const enrollmentChildPickupFields = [
  'id',
  'childFirstName',
  'childLastName',
  'childPickupSubmitted',
  'propositionType',
  'mainProposition.journey.date.returnDate',
  // a One to One Individuel enrollment carries its return date on its own proposition
  'otoIndividualProposition.returnDate'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentChildPickupType = SelectFields<
  Enrollment,
  typeof enrollmentChildPickupFields
>

// ------------------------- ENROLLMENT MEDICAL STATEMENT -------------------------
export const enrollmentMedicalStatementFields = [
  'id',
  'childHealthPapersSubmitted',
  'medicalInformations',
  'additionalMedicalInformations'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentMedicalStatementType = SelectFields<
  Enrollment,
  typeof enrollmentMedicalStatementFields
>

// ------------------------- ENROLLMENT CHILD IDENTITY -------------------------
export const enrollmentChildIdentityBaseFields = [
  'id',
  'childFirstName',
  'childLastName',
  'childBirthDate',
  'childGender',
  'childNationality',
  'residenceCountry',
  'childIdentityPapersSubmitted',
  'propositionType',
  'mainProposition.journey.date.product.country.id',
  // a One to One Individuel enrollment carries its destination on its own proposition
  'otoIndividualProposition.product.country.id'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentChildIdentityBaseType = SelectFields<
  Enrollment,
  typeof enrollmentChildIdentityBaseFields
>

// ------------------------- ENROLLMENT CLIENT IDENTITY -------------------------
export const enrollmentClientIdentityBaseFields = [
  'id',
  'childLastName',
  'childFirstName',
  'childBirthDate',
  'childGender',
  'childNationality',
  'residenceCountry'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentClientIdentityBaseType = SelectFields<
  Enrollment,
  typeof enrollmentClientIdentityBaseFields
>

// ------------------------- ENROLLMENT CHILD TITLE -------------------------
export const enrollmentChildTitleFields = [
  'id',
  'isAA',
  'childFirstName',
  'childLastName',
  'enrollmentNumber'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentChildTitleType = SelectFields<Enrollment, typeof enrollmentChildTitleFields>

// ------------------------- ENROLLMENT CHILD NAVBAR -------------------------
export const enrollmentChildNavbarFields = [
  'id',
  'isAA',
  'childFirstName',
  'childLastName',
  'medicalStatus',
  'medicalAttestationSending.signed',
  'medicalAttestationSending.type',
  'status'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentChildNavbarType = SelectFields<
  Enrollment,
  typeof enrollmentChildNavbarFields
>

// ------------------------- ENROLLMENT MANAGER SIDEBAR -------------------------
export const enrollmentManagerSidebarFields = [
  'id',
  'status',
  'propositionType',
  'mainProposition.mainTrip.hasTransport',
  'cseRegistration.cse.billingType'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentManagerSidebarType = SelectFields<
  Enrollment,
  typeof enrollmentManagerSidebarFields
>

// ------------------------- ENROLLMENT BASE CHILD HEALTH -------------------------
export const enrollmentMedicalLevelFields = [
  'id',
  'medicalLevel'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentMedicalLevelType = SelectFields<
  Enrollment,
  typeof enrollmentMedicalLevelFields
>

// ------------------------- ENROLLMENT PROFILE -------------------------
export const enrollmentProfileFields = [
  'id',
  'childInfoStatus',
  'childEmail',
  'childFirstName',
  'childLastName',
  'childPhoneNumber',
  'spokenLanguages',
  'childHostFamilyLetter',
  'parentSocialSecurityNumber',
  'childPreviousTravelOrganization',
  'residenceCountry',
  'isChildFirstLanguageStay',
  'doesChildSmoke',
  'holdingLECBrochure',
  'chosenCountryLanguageLevel',
  'familySituation',
  'howDidYouObtainLECBrochure.id',
  'howDidYouObtainLECBrochure.name',
  'parentAcceptGeneralPhilosophy',
  'childAcceptGeneralPhilosophy',
  'acceptMedicalGeneralTerms',
  'agreesToHouseRulesAndNoSmoking',
  'parentAcceptGeneralPhilosophySignedAt',
  'childAcceptGeneralPhilosophySignedAt',
  'acceptMedicalGeneralTermsSignedAt',
  'termsAcceptedDate',
  'identityPictureLocked',

  'secureFamilyImages.id',
  'secureFamilyImages.name',
  'secureFamilyImages.size',
  'secureFamilyImages.type',
  'secureFamilyImages.uploadId',
  'secureFamilyImages.caption',

  'characteristics.id',
  'characteristics.translations',

  'hobbies.id',
  'hobbies.name',

  'siblings.id',
  'siblings.name',
  'siblings.age',
  'siblings.gender'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentProfileType = SelectFields<Enrollment, typeof enrollmentProfileFields>

// ------------------------- ENROLLMENT UPDATE FORM -------------------------
export const enrollmentUpdateFormFields = [
  'id',
  'createdAt',
  'updatedAt',

  'childBirthDate',
  'childFirstName',
  'childLastName',
  'childEmail',
  'childNumberOfYearsInLanguage',
  'childPhoneNumber',
  'childGender',
  'childSchoolLevel',
  'childNationality',

  'parent1Email',
  'parent1FirstName',
  'parent1Gender',
  'parent1Job',
  'parent1LastName',
  'parent1PhoneNumber',
  'parent1PhoneNumber2',
  'parent2Email',
  'parent2FirstName',
  'parent2Gender',
  'parent2Job',
  'parent2LastName',
  'parent2PhoneNumber',
  'parent2PhoneNumber2',
  'contactPriority',
  'isAA',

  'address1',
  'address2',
  'city',
  'country',
  'postalCode',

  'personToContactInCaseOfEmergency',
  'personToContactInCaseOfEmergencyFullName',
  'personToContactInCaseOfEmergencyPhoneNumber',

  'parentAuthorizedToGoOutAlone',
  'howDidYouHearAboutUsOptionId',
  'medicalInformations',
  'medicalInformationsEn',
  'additionalMedicalInformations',
  'additionalMedicalInformationsEn',
  'canGoOutAlone',
  'medicalLevel',
  // the backend decides whether LEC may write the main health field, from origin, status and
  // confirmedAt
  'origin',
  'canEditMainMedicalInformations',

  'cseRegistration.extraOptionLabel',

  'declaredFriends.id',
  'declaredFriends.firstName',
  'declaredFriends.lastName',
  'declaredFriends.type',

  'cancellationReason',
  'confirmedAt',
  'paymentStatus',
  'cseStatus',
  'medicalStatus',
  'medicalAttestationSending.signed',
  'medicalAttestationSending.type',
  'status',
  'createdBy.firstName',
  'createdBy.lastName',
  'attributedUser.id',
  'attributedUser.firstName',
  'attributedUser.lastName',

  'mainProposition.id',

  'mainProposition.journey.id',
  'mainProposition.journey.reference',
  'mainProposition.journey.mandatoryUnderAge',
  'mainProposition.journey.mainOption.id',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.journey.date.id',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.durationInDays',
  'mainProposition.journey.date.product.id',
  'mainProposition.journey.date.product.country.id',
  'mainProposition.journey.date.product.country.name',
  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.numberOfHoursPerWeek',

  'mainProposition.insurances.id',
  'mainProposition.insurances.insurance.id',
  'mainProposition.insurances.insurance.name',
  'mainProposition.insurances.insurance.type',

  'mainProposition.options.option.id',
  'mainProposition.options.option.translations',

  'mainProposition.mainTrip.id',
  'mainProposition.mainTrip.arrival.translations',
  'mainProposition.mainTrip.departure.translations',
  'mainProposition.mainTrip.transportMeans.translations',
  'mainProposition.mainTrip.transportMeans.picto.path',
  'mainProposition.mainTrip.journeyPlace.name',

  'mainProposition.secondaryTrip.id',
  'mainProposition.secondaryTrip.arrival.translations',
  'mainProposition.secondaryTrip.departure.translations',
  'mainProposition.secondaryTrip.transportMeans.translations',
  'mainProposition.secondaryTrip.transportMeans.picto.path',
  'mainProposition.secondaryTrip.journeyPlace.name',

  'mainProposition.preferredPlace.id',
  'mainProposition.preferredPlace.name',

  'mainProposition.journeyTripPlace.id',
  'mainProposition.journeyTripPlace.translations',

  'mainProposition.pricing.includeFamilyDiscount',
  'mainProposition.pricing.includeApplicationFees',
  'mainProposition.promoCode.id',
  'mainProposition.promoCode.name',
  'mainProposition.promoCode.code',
  'mainProposition.promoCode.discount',

  'propositionType',
  'otoIndividualProposition.id',
  'otoIndividualProposition.numberOfWeeks',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.numberOfHours',
  'otoIndividualProposition.journeyPrice',
  'otoIndividualProposition.optionPrice',
  'otoIndividualProposition.transferMethod',
  'otoIndividualProposition.transferPrice',
  'otoIndividualProposition.pricing.includeApplicationFees',
  'otoIndividualProposition.pricing.includeFamilyDiscount',
  'otoIndividualProposition.pricing.promoCodeDiscount',
  'otoIndividualProposition.pricing.promoCodeType',
  'otoIndividualProposition.promoCode.id',
  'otoIndividualProposition.promoCode.code',
  'otoIndividualProposition.promoCode.name',
  'otoIndividualProposition.promoCode.discount',
  'otoIndividualProposition.promoCode.type',
  'otoIndividualProposition.product.id',
  'otoIndividualProposition.product.name',
  'otoIndividualProposition.product.country.id',
  'otoIndividualProposition.product.country.name',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.regionOfStay.id',
  'otoIndividualProposition.regionOfStay.weeklyPrice',
  'otoIndividualProposition.regionOfStay.optionPrice',
  'otoIndividualProposition.regionOfStay.hasAgeLimit',
  'otoIndividualProposition.regionOfStay.mandatoryUnderAge',
  'otoIndividualProposition.regionOfStay.option.id',
  'otoIndividualProposition.regionOfStay.option.translations',
  'otoIndividualProposition.regionOfStay.place.id',
  'otoIndividualProposition.regionOfStay.place.name',
  'otoIndividualProposition.regionOfStay.place.price',
  'otoIndividualProposition.regionOfStay.place.hasAgeLimit',
  'otoIndividualProposition.regionOfStay.place.mandatoryUnderAge',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.id',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.title',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.code',
  'otoIndividualProposition.arrivalPlace.id',
  'otoIndividualProposition.arrivalPlace.title',
  'otoIndividualProposition.arrivalPlace.code',
  'otoIndividualProposition.option.id',
  'otoIndividualProposition.option.translations',
  'otoIndividualProposition.insurances.id',
  'otoIndividualProposition.insurances.price',
  'otoIndividualProposition.insurances.insurance.id',
  'otoIndividualProposition.insurances.insurance.name',
  'otoIndividualProposition.insurances.insurance.type',

  'cseRegistration.cse.code'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentUpdateFormType = SelectFields<Enrollment, typeof enrollmentUpdateFormFields>

// ------------------------- ENROLLMENT QUOTATION -------------------------
export const enrollmentQuotationFields = [
  'id',
  'childFirstName',
  'childLastName',
  'enrollmentNumber',
  'isAA',

  'parent1Email',
  'parent1FirstName',
  'parent1Gender',
  'parent1LastName',
  'parent1PhoneNumber',

  'declaredFriends.id',
  'declaredFriends.firstName',
  'declaredFriends.lastName',
  'declaredFriends.type',

  'mainProposition.id',

  'mainProposition.journey.id',
  'mainProposition.journey.reference',
  'mainProposition.journey.mandatoryUnderAge',
  'mainProposition.journey.mainOption.id',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.journey.date.id',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.durationInDays',
  'mainProposition.journey.date.product.id',
  'mainProposition.journey.date.product.country.id',
  'mainProposition.journey.date.product.country.name',
  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.numberOfHoursPerWeek',

  'mainProposition.insurances.id',
  'mainProposition.insurances.insurance.id',
  'mainProposition.insurances.insurance.name',
  'mainProposition.insurances.insurance.type',

  'mainProposition.options.option.id',
  'mainProposition.options.option.translations',

  'mainProposition.mainTrip.id',
  'mainProposition.mainTrip.arrival.translations',
  'mainProposition.mainTrip.departure.translations',
  'mainProposition.mainTrip.transportMeans.translations',
  'mainProposition.mainTrip.transportMeans.picto.path',
  'mainProposition.mainTrip.journeyPlace.name',

  'mainProposition.secondaryTrip.id',
  'mainProposition.secondaryTrip.arrival.translations',
  'mainProposition.secondaryTrip.departure.translations',
  'mainProposition.secondaryTrip.transportMeans.translations',
  'mainProposition.secondaryTrip.transportMeans.picto.path',
  'mainProposition.secondaryTrip.journeyPlace.name',

  'mainProposition.preferredPlace.id',
  'mainProposition.preferredPlace.name',

  'mainProposition.journeyTripPlace.id',
  'mainProposition.journeyTripPlace.translations',

  'mainProposition.pricing.includeFamilyDiscount',
  'mainProposition.pricing.includeApplicationFees',
  'mainProposition.promoCode.id',
  'mainProposition.promoCode.name',
  'mainProposition.promoCode.code',
  'mainProposition.promoCode.discount',

  'propositionType',
  'otoIndividualProposition.id',
  'otoIndividualProposition.numberOfWeeks',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.numberOfHours',
  'otoIndividualProposition.journeyPrice',
  'otoIndividualProposition.optionPrice',
  'otoIndividualProposition.transferMethod',
  'otoIndividualProposition.transferPrice',
  'otoIndividualProposition.pricing.includeApplicationFees',
  'otoIndividualProposition.pricing.includeFamilyDiscount',
  'otoIndividualProposition.pricing.promoCodeDiscount',
  'otoIndividualProposition.pricing.promoCodeType',
  'otoIndividualProposition.promoCode.id',
  'otoIndividualProposition.promoCode.code',
  'otoIndividualProposition.promoCode.name',
  'otoIndividualProposition.promoCode.discount',
  'otoIndividualProposition.promoCode.type',
  'otoIndividualProposition.product.id',
  'otoIndividualProposition.product.name',
  'otoIndividualProposition.product.country.id',
  'otoIndividualProposition.product.country.name',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.period.id',
  'otoIndividualProposition.period.color',
  'otoIndividualProposition.regionOfStay.id',
  'otoIndividualProposition.regionOfStay.weeklyPrice',
  'otoIndividualProposition.regionOfStay.optionPrice',
  'otoIndividualProposition.regionOfStay.hasAgeLimit',
  'otoIndividualProposition.regionOfStay.mandatoryUnderAge',
  'otoIndividualProposition.regionOfStay.option.id',
  'otoIndividualProposition.regionOfStay.option.translations',
  'otoIndividualProposition.regionOfStay.place.id',
  'otoIndividualProposition.regionOfStay.place.name',
  'otoIndividualProposition.regionOfStay.place.price',
  'otoIndividualProposition.regionOfStay.place.hasAgeLimit',
  'otoIndividualProposition.regionOfStay.place.mandatoryUnderAge',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.id',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.title',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.code',
  'otoIndividualProposition.arrivalPlace.id',
  'otoIndividualProposition.arrivalPlace.title',
  'otoIndividualProposition.arrivalPlace.code',
  'otoIndividualProposition.option.id',
  'otoIndividualProposition.option.translations',
  'otoIndividualProposition.insurances.id',
  'otoIndividualProposition.insurances.price',
  'otoIndividualProposition.insurances.insurance.id',
  'otoIndividualProposition.insurances.insurance.name',
  'otoIndividualProposition.insurances.insurance.type',

  'cseStatus',
  'cseRegistration.cse.code'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentQuotationType = SelectFields<Enrollment, typeof enrollmentQuotationFields>

// ------------------------- ENROLLMENT PROFILE CONTEXT -------------------------
// the places other students held that were linked to this enrollment: the reverse side of
// "Amis associés", only shown to LEC
export const enrollmentProfileAssociatedFriendsFields = [
  'blockedByFriends.id',
  'blockedByFriends.type',
  'blockedByFriends.originalEnrollment.id',
  'blockedByFriends.originalEnrollment.childFirstName',
  'blockedByFriends.originalEnrollment.childLastName'
] as const satisfies FieldsSelector<Enrollment>

export const enrollmentProfileContextMainFields = [
  'id',
  'createdAt',
  'updatedAt',
  'isAA',

  'enrollmentNumber',
  'childFirstName',
  'childLastName',
  'childEmail',
  'childBirthDate',
  'childGender',
  'confirmedAt',
  'paymentStatus',
  'medicalStatus',
  'childNationality',
  'childSchoolLevel',
  'childNumberOfYearsInLanguage',
  'childPhoneNumber',

  'childIdentityPapersSubmitted',
  'childPickupSubmitted',

  'secureFamilyImages.id',
  'secureFamilyImages.name',
  'secureFamilyImages.size',
  'secureFamilyImages.type',
  'secureFamilyImages.uploadId',

  'languageLevel',
  'isAA',
  'status',
  'paymentStatus',
  'optinForAutomaticDebit',

  'identityPictureLocked',
  'childHealthPapersSubmitted',
  'childInfoStatus',
  'canGoOutAlone',
  'authorizedAt',

  'parentAuthorizedToGoOutAlone',
  'parentCancellationReason',
  'parentCancellationStatus',
  'parent1Gender',
  'parent1FirstName',
  'parent1LastName',
  'parent1Job',
  'parent1PhoneNumber',
  'parent1PhoneNumber2',
  'parent1Email',
  'parent2Gender',
  'parent2FirstName',
  'parent2LastName',
  'parent2Job',
  'parent2PhoneNumber',
  'parent2PhoneNumber2',
  'parent2Email',
  'contactPriority',
  'personToContactInCaseOfEmergencyFullName',
  'personToContactInCaseOfEmergency',
  'personToContactInCaseOfEmergencyPhoneNumber',

  'address1',
  'address2',
  'city',
  'postalCode',
  'country',

  'medicalLevel',
  'medicalStatus',
  'medicalInformations',
  'additionalMedicalInformations',
  'declarationSignedAt',
  'medicalInformationsEn',
  'additionalMedicalInformationsEn',

  'languageTestSubmissionLocked',
  'spokenLanguages',

  'childHostFamilyLetter',
  'parentSocialSecurityNumber',
  'childPreviousTravelOrganization',
  'residenceCountry',
  'isChildFirstLanguageStay',
  'doesChildSmoke',
  'holdingLECBrochure',
  'chosenCountryLanguageLevel',
  'familySituation',

  'childAcceptGeneralPhilosophy',
  'acceptMedicalGeneralTerms',
  'agreesToHouseRulesAndNoSmoking',
  'parentAcceptGeneralPhilosophy',
  'parentAcceptGeneralPhilosophySignedAt',
  'childAcceptGeneralPhilosophySignedAt',
  'acceptMedicalGeneralTermsSignedAt',

  'secureIdentityPicture.id',
  'secureIdentityPicture.name',
  'secureIdentityPicture.size',
  'secureIdentityPicture.type',
  'secureIdentityPicture.uploadId',

  'attributedUser.id',
  'attributedUser.firstName',
  'attributedUser.lastName',

  'status',

  // the backend decides whether the admin can connect as the client, from the linked users' role
  'users.role',
  'canConnectAsClient',

  'characteristics.id',
  'characteristics.translations',

  'hobbies.id',
  'hobbies.name',

  'siblings.id',
  'siblings.name',

  'medicalAttestationSending.acceptedAt',
  'medicalAttestationSending.signed',
  'medicalAttestationSending.type',

  'languageTestSubmission.submittedAt',
  'languageTestSubmission.level',
  'languageTestSubmission.score',

  'cseStatus',
  'cseRegistration.extraOptionLabel',
  'cseRegistration.cse.code',
  'cseRegistration.cse.extraOptionEnabled',
  'cseRegistration.cse.extraOptionLabel',

  'howDidYouObtainLECBrochure.id',
  'howDidYouObtainLECBrochure.name',

  'enrollmentPickup.id',
  'enrollmentPickup.firstName',
  'enrollmentPickup.lastName',
  'enrollmentPickup.person',
  'enrollmentPickup.phoneNumber',

  'declaredFriends.id',
  'declaredFriends.firstName',
  'declaredFriends.lastName',
  'declaredFriends.type',

  'blockedFriends.id',
  'blockedFriends.firstName',
  'blockedFriends.lastName',
  'blockedFriends.type',

  ...enrollmentProfileAssociatedFriendsFields
] as const satisfies FieldsSelector<Enrollment>

// a One to One Individuel enrollment has no journey, no trip and no center: everything the
// profile and the client space need about its stay lives on its own proposition
export const enrollmentOtoIndividualStayFields = [
  'propositionType',
  'otoIndividualProposition.id',
  'otoIndividualProposition.numberOfWeeks',
  'otoIndividualProposition.numberOfHours',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.transferMethod',
  'otoIndividualProposition.period.id',
  'otoIndividualProposition.period.color',
  'otoIndividualProposition.product.id',
  'otoIndividualProposition.product.name',
  'otoIndividualProposition.product.country.id',
  'otoIndividualProposition.product.country.name',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.arrivalPlace.id',
  'otoIndividualProposition.arrivalPlace.title',
  'otoIndividualProposition.arrivalPlace.code',
  'otoIndividualProposition.option.id',
  'otoIndividualProposition.option.translations',
  'otoIndividualProposition.insurances.id',
  'otoIndividualProposition.insurances.price',
  'otoIndividualProposition.insurances.insurance.id',
  'otoIndividualProposition.insurances.insurance.name',
  'otoIndividualProposition.insurances.insurance.type',

  // everything below is what the séjour / options / assurances edit modals need to re-seed the
  // proposition form: without it the proposition cannot be mapped back to a form input
  'otoIndividualProposition.journeyPrice',
  'otoIndividualProposition.optionPrice',
  'otoIndividualProposition.transferPrice',
  'otoIndividualProposition.pricing.includeApplicationFees',
  'otoIndividualProposition.pricing.includeFamilyDiscount',
  'otoIndividualProposition.pricing.promoCodeDiscount',
  'otoIndividualProposition.pricing.promoCodeType',
  'otoIndividualProposition.promoCode.id',
  'otoIndividualProposition.promoCode.code',
  'otoIndividualProposition.promoCode.name',
  'otoIndividualProposition.promoCode.discount',
  'otoIndividualProposition.promoCode.type',
  'otoIndividualProposition.regionOfStay.id',
  'otoIndividualProposition.regionOfStay.option.id',
  'otoIndividualProposition.regionOfStay.option.translations',
  'otoIndividualProposition.regionOfStay.place.id',
  'otoIndividualProposition.regionOfStay.place.name',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.id',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.title',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.code'
] as const satisfies FieldsSelector<Enrollment>

// the unit prices and the age rules of the région du séjour: the backend only serves them to LEC
// and to the prestataire, the parents read the amounts computed on the proposition itself
export const enrollmentOtoIndividualStayInternalFields = [
  'otoIndividualProposition.regionOfStay.weeklyPrice',
  'otoIndividualProposition.regionOfStay.optionPrice',
  'otoIndividualProposition.regionOfStay.hasAgeLimit',
  'otoIndividualProposition.regionOfStay.mandatoryUnderAge',
  'otoIndividualProposition.regionOfStay.place.price',
  'otoIndividualProposition.regionOfStay.place.hasAgeLimit',
  'otoIndividualProposition.regionOfStay.place.mandatoryUnderAge'
] as const satisfies FieldsSelector<Enrollment>

// the host family address is only shown on the enrollment profile, the client space does not load it
export const enrollmentProfileHostFamilyAddressFields = [
  'enrollmentCenter.hostFamily.address1',
  'enrollmentCenter.hostFamily.address2',
  'enrollmentCenter.hostFamily.zipCode',
  'enrollmentCenter.hostFamily.city'
] as const satisfies FieldsSelector<Enrollment>

export const enrollmentProfileContextJourneyFields = [
  'id',
  ...enrollmentOtoIndividualStayFields,
  'enrollmentCenter.center.id',
  'enrollmentCenter.center.name',
  'enrollmentCenter.center.locked',
  'enrollmentCenter.center.accomodation',
  'enrollmentCenter.center.olJourneyPlace.isSameAddresForTuition',
  'enrollmentCenter.center.olJourneyPlace.address1',
  'enrollmentCenter.center.olJourneyPlace.tuitionAddress1',
  'enrollmentCenter.center.journeyPlace.name',
  'enrollmentCenter.center.journeyPlace.address',
  'enrollmentCenter.center.journeyPlace.country.name',
  'enrollmentCenter.center.diffusedCenterManager.facNumber',
  'enrollmentCenter.center.diffusedCenterManager.staff.firstName',
  'enrollmentCenter.center.diffusedCenterManager.staff.lastName',
  'enrollmentCenter.center.diffusedCenterManager.staff.phoneNumber',
  'enrollmentCenter.hostFamily.id',
  'enrollmentCenter.hostFamily.familyName',
  ...enrollmentProfileHostFamilyAddressFields,

  'mainProposition.id',
  'mainProposition.journey.id',
  'mainProposition.journey.reference',
  'mainProposition.journey.mandatoryUnderAge',
  'mainProposition.journey.mainOption.id',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.journey.date.id',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.durationInDays',
  'mainProposition.journey.date.period.id',
  'mainProposition.journey.date.period.color',
  'mainProposition.journey.date.period.name',
  'mainProposition.journey.date.period.nameEn',
  'mainProposition.journey.date.product.id',
  'mainProposition.journey.date.product.country.id',
  'mainProposition.journey.date.product.country.name',
  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.numberOfHoursPerWeek',
  'mainProposition.journey.date.product.noTransportGuide',

  'mainProposition.insurances.id',
  'mainProposition.insurances.insurance.id',
  'mainProposition.insurances.insurance.name',
  'mainProposition.insurances.insurance.type',

  'mainProposition.options.option.id',
  'mainProposition.options.option.translations',

  'mainProposition.preferredPlace.id',
  'mainProposition.preferredPlace.name',

  'mainProposition.journeyTripPlace.id',
  'mainProposition.journeyTripPlace.translations',

  'mainProposition.mainTrip.id',
  'mainProposition.mainTrip.hasTransport',
  'mainProposition.mainTrip.arrival.translations',
  'mainProposition.mainTrip.departure.translations',
  'mainProposition.mainTrip.transportMeans.translations',
  'mainProposition.mainTrip.transportMeans.picto.path',
  'mainProposition.mainTrip.journeyPlace.name',

  'mainProposition.secondaryTrip.id',
  'mainProposition.secondaryTrip.arrival.translations',
  'mainProposition.secondaryTrip.departure.translations',
  'mainProposition.secondaryTrip.transportMeans.translations',
  'mainProposition.secondaryTrip.transportMeans.picto.path',
  'mainProposition.secondaryTrip.journeyPlace.name',

  'mainProposition.familyArrangement'
] as const satisfies FieldsSelector<Enrollment>

// what LEC and the prestataire read on top of the shared profile: the catalogue prices behind the
// One to One Individuel proposition, which the edit wizard is seeded from
export const enrollmentProfileContextJourneyInternalFields = [
  ...enrollmentProfileContextJourneyFields,
  ...enrollmentOtoIndividualStayInternalFields
] as const satisfies FieldsSelector<Enrollment>

// typed on the widest selection: the parents simply receive the internal fields as undefined
export const enrollmentProfileContextFields = [
  ...enrollmentProfileContextMainFields,
  ...enrollmentProfileContextJourneyInternalFields
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentProfileContextType = SelectFields<
  Enrollment,
  typeof enrollmentProfileContextFields
>

// ------------------------- ENROLLMENT DUPLICATION -------------------------
export const enrolmentDuplicationFields = [
  'id',

  'childFirstName',
  'childLastName',
  'enrollmentNumber',
  'isAA',

  'parent1Email',
  'parent1FirstName',
  'parent1Gender',
  'parent1Job',
  'parent1LastName',
  'parent1PhoneNumber',
  'parent1PhoneNumber2',

  'parent2Email',
  'parent2FirstName',
  'parent2Gender',
  'parent2Job',
  'parent2LastName',
  'parent2PhoneNumber',
  'parent2PhoneNumber2',

  'contactPriority',

  'address1',
  'address2',
  'city',
  'country',
  'postalCode',

  'personToContactInCaseOfEmergency',
  'personToContactInCaseOfEmergencyFullName',
  'personToContactInCaseOfEmergencyPhoneNumber',

  'howDidYouHearAboutUsOptionId',

  'declaredFriends.id',
  'declaredFriends.firstName',
  'declaredFriends.lastName',
  'declaredFriends.type',

  'cseRegistration.cse.code',

  'mainProposition.id',
  'mainProposition.journey.id',
  'mainProposition.journey.reference',
  'mainProposition.journey.mandatoryUnderAge',
  'mainProposition.journey.mainOption.id',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.journey.date.id',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.durationInDays',
  'mainProposition.journey.date.product.id',
  'mainProposition.journey.date.product.country.id',
  'mainProposition.journey.date.product.country.name',
  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.numberOfHoursPerWeek',

  'mainProposition.insurances.id',
  'mainProposition.insurances.insurance.id',
  'mainProposition.insurances.insurance.name',
  'mainProposition.insurances.insurance.type',

  'mainProposition.options.option.id',
  'mainProposition.options.option.translations',

  'mainProposition.mainTrip.id',
  'mainProposition.mainTrip.arrival.translations',
  'mainProposition.mainTrip.departure.translations',
  'mainProposition.mainTrip.transportMeans.translations',
  'mainProposition.mainTrip.transportMeans.picto.path',
  'mainProposition.mainTrip.journeyPlace.name',

  'mainProposition.secondaryTrip.id',
  'mainProposition.secondaryTrip.arrival.translations',
  'mainProposition.secondaryTrip.departure.translations',
  'mainProposition.secondaryTrip.transportMeans.translations',
  'mainProposition.secondaryTrip.transportMeans.picto.path',
  'mainProposition.secondaryTrip.journeyPlace.name',

  'mainProposition.preferredPlace.id',
  'mainProposition.preferredPlace.name',

  'mainProposition.journeyTripPlace.id',
  'mainProposition.journeyTripPlace.translations',

  'mainProposition.pricing.includeFamilyDiscount',
  'mainProposition.pricing.includeApplicationFees',
  'mainProposition.promoCode.id',
  'mainProposition.promoCode.name',
  'mainProposition.promoCode.code',
  'mainProposition.promoCode.discount',
  'mainProposition.familyArrangement',

  'propositionType',
  'otoIndividualProposition.id',
  'otoIndividualProposition.numberOfWeeks',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.numberOfHours',
  'otoIndividualProposition.journeyPrice',
  'otoIndividualProposition.optionPrice',
  'otoIndividualProposition.transferMethod',
  'otoIndividualProposition.transferPrice',
  'otoIndividualProposition.pricing.includeApplicationFees',
  'otoIndividualProposition.pricing.includeFamilyDiscount',
  'otoIndividualProposition.pricing.promoCodeDiscount',
  'otoIndividualProposition.pricing.promoCodeType',
  'otoIndividualProposition.promoCode.id',
  'otoIndividualProposition.promoCode.code',
  'otoIndividualProposition.promoCode.name',
  'otoIndividualProposition.promoCode.discount',
  'otoIndividualProposition.promoCode.type',
  'otoIndividualProposition.product.id',
  'otoIndividualProposition.product.name',
  'otoIndividualProposition.product.country.id',
  'otoIndividualProposition.product.country.name',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.regionOfStay.id',
  'otoIndividualProposition.regionOfStay.weeklyPrice',
  'otoIndividualProposition.regionOfStay.optionPrice',
  'otoIndividualProposition.regionOfStay.hasAgeLimit',
  'otoIndividualProposition.regionOfStay.mandatoryUnderAge',
  'otoIndividualProposition.regionOfStay.option.id',
  'otoIndividualProposition.regionOfStay.option.translations',
  'otoIndividualProposition.regionOfStay.place.id',
  'otoIndividualProposition.regionOfStay.place.name',
  'otoIndividualProposition.regionOfStay.place.price',
  'otoIndividualProposition.regionOfStay.place.hasAgeLimit',
  'otoIndividualProposition.regionOfStay.place.mandatoryUnderAge',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.id',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.title',
  'otoIndividualProposition.regionOfStay.place.arrivalPoint.code',
  'otoIndividualProposition.arrivalPlace.id',
  'otoIndividualProposition.arrivalPlace.title',
  'otoIndividualProposition.arrivalPlace.code',
  'otoIndividualProposition.option.id',
  'otoIndividualProposition.option.translations',
  'otoIndividualProposition.insurances.id',
  'otoIndividualProposition.insurances.price',
  'otoIndividualProposition.insurances.insurance.id',
  'otoIndividualProposition.insurances.insurance.name',
  'otoIndividualProposition.insurances.insurance.type'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentDuplicationType = SelectFields<Enrollment, typeof enrolmentDuplicationFields>

// ------------------------- ENROLLMENT CLIENT CONTEXT -------------------------
export const enrollmentClientContextMainFields = [
  'id',
  'enrollmentNumber',
  'confirmedAt',
  'childFirstName',
  'childLastName',
  'childBirthDate',
  'childGender',
  'childEmail',
  'childNationality',
  'childSchoolLevel',
  'childNumberOfYearsInLanguage',
  'childPhoneNumber',

  'childIdentityPapersSubmitted',
  'childPickupSubmitted',

  'languageLevel',
  'isAA',
  'status',
  'paymentStatus',
  'optinForAutomaticDebit',

  'identityPictureLocked',
  'childHealthPapersSubmitted',
  'childInfoStatus',
  'canGoOutAlone',
  'authorizedAt',

  'parentAuthorizedToGoOutAlone',
  'parentCancellationReason',
  'parentCancellationStatus',
  'parent1Gender',
  'parent1FirstName',
  'parent1LastName',
  'parent1Job',
  'parent1PhoneNumber',
  'parent1PhoneNumber2',
  'parent1Email',
  'parent2Gender',
  'parent2FirstName',
  'parent2LastName',
  'parent2Job',
  'parent2PhoneNumber',
  'parent2PhoneNumber2',
  'parent2Email',
  'contactPriority',
  'personToContactInCaseOfEmergencyFullName',
  'personToContactInCaseOfEmergency',
  'personToContactInCaseOfEmergencyPhoneNumber',

  'address1',
  'address2',
  'city',
  'postalCode',
  'country',

  'medicalLevel',
  'medicalStatus',
  'medicalInformations',
  'additionalMedicalInformations',
  'declarationSignedAt',
  'medicalInformationsEn',
  'additionalMedicalInformationsEn',

  'languageTestSubmissionLocked',
  'spokenLanguages',

  'childHostFamilyLetter',
  'parentSocialSecurityNumber',
  'childPreviousTravelOrganization',
  'residenceCountry',
  'isChildFirstLanguageStay',
  'doesChildSmoke',
  'holdingLECBrochure',
  'chosenCountryLanguageLevel',
  'familySituation',

  'childAcceptGeneralPhilosophy',
  'acceptMedicalGeneralTerms',
  'agreesToHouseRulesAndNoSmoking',
  'parentAcceptGeneralPhilosophy',
  'parentAcceptGeneralPhilosophySignedAt',
  'childAcceptGeneralPhilosophySignedAt',
  'acceptMedicalGeneralTermsSignedAt',

  'secureFamilyImages.id',
  'secureFamilyImages.name',
  'secureFamilyImages.size',
  'secureFamilyImages.type',
  'secureFamilyImages.uploadId',

  'secureIdentityPicture.id',
  'secureIdentityPicture.name',
  'secureIdentityPicture.size',
  'secureIdentityPicture.type',
  'secureIdentityPicture.uploadId',

  'characteristics.id',
  'characteristics.translations',

  'hobbies.id',
  'hobbies.name',

  'siblings.id',
  'siblings.name',

  'medicalAttestationSending.acceptedAt',
  'medicalAttestationSending.signed',
  'medicalAttestationSending.type',

  'languageTestSubmission.submittedAt',
  'languageTestSubmission.level',
  'languageTestSubmission.score',

  'cseStatus',
  'cseRegistration.extraOptionLabel',
  'cseRegistration.cse.code',
  'cseRegistration.cse.extraOptionEnabled',
  'cseRegistration.cse.extraOptionLabel',

  'howDidYouObtainLECBrochure.id',
  'howDidYouObtainLECBrochure.name',

  'enrollmentPickup.id',
  'enrollmentPickup.firstName',
  'enrollmentPickup.lastName',
  'enrollmentPickup.person',
  'enrollmentPickup.phoneNumber',

  'declaredFriends.id',
  'declaredFriends.firstName',
  'declaredFriends.lastName',
  'declaredFriends.type',

  'blockedFriends.id',
  'blockedFriends.firstName',
  'blockedFriends.lastName',
  'blockedFriends.type'
] as const satisfies FieldsSelector<Enrollment>

export const enrollmentClientContextJourneyFields = [
  'id',
  ...enrollmentOtoIndividualStayFields,
  'enrollmentCenter.center.id',
  'enrollmentCenter.center.name',
  'enrollmentCenter.center.locked',
  'enrollmentCenter.center.accomodation',
  'enrollmentCenter.center.olJourneyPlace.isSameAddresForTuition',
  'enrollmentCenter.center.olJourneyPlace.address1',
  'enrollmentCenter.center.olJourneyPlace.tuitionAddress1',
  'enrollmentCenter.center.journeyPlace.name',
  'enrollmentCenter.center.journeyPlace.address',
  'enrollmentCenter.center.journeyPlace.country.name',
  'enrollmentCenter.center.diffusedCenterManager.facNumber',
  'enrollmentCenter.center.diffusedCenterManager.staff.firstName',
  'enrollmentCenter.center.diffusedCenterManager.staff.lastName',
  'enrollmentCenter.center.diffusedCenterManager.staff.phoneNumber',
  'enrollmentCenter.hostFamily.id',
  'enrollmentCenter.hostFamily.familyName',

  'mainProposition.id',
  'mainProposition.journey.id',
  'mainProposition.journey.reference',
  'mainProposition.journey.mandatoryUnderAge',
  'mainProposition.journey.mainOption.id',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.journey.date.id',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.durationInDays',
  'mainProposition.journey.date.period.id',
  'mainProposition.journey.date.period.color',
  'mainProposition.journey.date.period.name',
  'mainProposition.journey.date.period.nameEn',
  'mainProposition.journey.date.product.id',
  'mainProposition.journey.date.product.country.id',
  'mainProposition.journey.date.product.country.name',
  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.numberOfHoursPerWeek',
  'mainProposition.journey.date.product.noTransportGuide',
  'mainProposition.journey.date.product.accomodationCategory',

  'mainProposition.insurances.id',
  'mainProposition.insurances.insurance.id',
  'mainProposition.insurances.insurance.name',
  'mainProposition.insurances.insurance.type',

  'mainProposition.options.option.id',
  'mainProposition.options.option.translations',

  'mainProposition.preferredPlace.id',
  'mainProposition.preferredPlace.name',

  'mainProposition.mainTrip.id',
  'mainProposition.mainTrip.hasTransport',
  'mainProposition.mainTrip.arrival.translations',
  'mainProposition.mainTrip.departure.translations',
  'mainProposition.mainTrip.transportMeans.translations',
  'mainProposition.mainTrip.transportMeans.picto.path',
  'mainProposition.mainTrip.journeyPlace.name',

  'mainProposition.secondaryTrip.id',
  'mainProposition.secondaryTrip.arrival.translations',
  'mainProposition.secondaryTrip.departure.translations',
  'mainProposition.secondaryTrip.transportMeans.translations',
  'mainProposition.secondaryTrip.transportMeans.picto.path',
  'mainProposition.secondaryTrip.journeyPlace.name',

  'mainProposition.familyArrangement'
] as const satisfies FieldsSelector<Enrollment>

// what LEC and the prestataire read on top of the shared context: the catalogue prices behind the
// One to One Individuel proposition, which the edit wizard of the profile is seeded from
export const enrollmentClientContextJourneyInternalFields = [
  ...enrollmentClientContextJourneyFields,
  ...enrollmentOtoIndividualStayInternalFields
] as const satisfies FieldsSelector<Enrollment>

export const enrollmentClientContextFields = [
  ...enrollmentClientContextMainFields,
  ...enrollmentClientContextJourneyFields
] as const satisfies FieldsSelector<Enrollment>

// the internal fields are only present for LEC and the prestataire, so they stay optional here
export type EnrollmentClientContextType = SelectFields<
  Enrollment,
  typeof enrollmentClientContextFields
> &
  DeepPartial<SelectFields<Enrollment, typeof enrollmentOtoIndividualStayInternalFields>> &
  DeepPartial<SelectFields<Enrollment, typeof enrollmentProfileHostFamilyAddressFields>> &
  DeepPartial<SelectFields<Enrollment, typeof enrollmentProfileAssociatedFriendsFields>>

// the journey selection of the shared context, by who asks for it
export const getEnrollmentClientContextJourneyFields = (role: RoleEnum) =>
  role === RoleEnum.CLIENT
    ? enrollmentClientContextJourneyFields
    : enrollmentClientContextJourneyInternalFields

// ------------------------- ENROLLMENT FRENCH LEADER -------------------------
export const enrollmentFrenchLeaderFields = [
  'id',
  'enrollmentCenter.hostFamily.familyName',
  'enrollmentCenter.hostFamily.address1',
  'enrollmentCenter.hostFamily.address2',
  'enrollmentCenter.hostFamily.zipCode',
  'enrollmentCenter.hostFamily.city',
  'enrollmentCenter.hostFamily.country',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentFrenchLeaderType = SelectFields<
  Enrollment,
  typeof enrollmentFrenchLeaderFields
>

// ------------------------- ENROLLMENT CLIENT REDIRECTION -------------------------
export const enrollmentClientRedirectionFields = [
  'id',
  'childFirstName',
  'childLastName',
  'childBirthDate',
  'status',
  'enrollmentNumber',
  'status',
  'propositionType',

  'mainProposition.mainTrip.departure.translations',
  'mainProposition.mainTrip.arrival.translations',
  'mainProposition.mainTrip.journeyPlace.name',
  'mainProposition.mainTrip.transportMeans.translations',

  'mainProposition.secondaryTrip.departure.translations',
  'mainProposition.secondaryTrip.arrival.translations',
  'mainProposition.secondaryTrip.journeyPlace.name',
  'mainProposition.secondaryTrip.transportMeans.translations',

  'mainProposition.options.option.translations',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.period.color',
  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.product.country.name',
  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',

  // One to One Individuel enrollments carry no journey nor trip
  'otoIndividualProposition.numberOfWeeks',
  'otoIndividualProposition.numberOfHours',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.transferMethod',
  'otoIndividualProposition.period.color',
  'otoIndividualProposition.product.name',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.product.country.name',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.option.translations',
  'otoIndividualProposition.arrivalPlace.title',
  'otoIndividualProposition.regionOfStay.place.name'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentClientRedirectionType = SelectFields<
  Enrollment,
  typeof enrollmentClientRedirectionFields
>

// ------------------------- ENROLLMENT AFFECTED ENROLLMENT FORM -------------------------
export const enrollmentAffectedEnrollmentFormFields = [
  'id',
  'status',
  'childFirstName',
  'childLastName',
  'mainProposition.mainTrip.journeyPlace.name',

  'mainProposition.mainTrip.departure.translations',
  'mainProposition.mainTrip.arrival.translations',
  'mainProposition.mainTrip.journeyPlace.name',
  'mainProposition.mainTrip.transportMeans.translations',
  'mainProposition.mainTrip.hasTransport',

  'mainProposition.secondaryTrip.departure.translations',
  'mainProposition.secondaryTrip.arrival.translations',
  'mainProposition.secondaryTrip.journeyPlace.name',
  'mainProposition.secondaryTrip.transportMeans.translations',

  'mainProposition.journey.id',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.accomodationCategory',
  'mainProposition.journey.date.product.numberOfHoursPerWeek',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.options.option.translations',
  'mainProposition.mainTrip.id',
  'mainProposition.mainTrip.linkedToMainTripId',
  'mainProposition.secondaryTrip.id',
  'mainProposition.preferredPlace.name',

  'propositionType',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.period.color',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.option.translations',
  'otoIndividualProposition.numberOfHours',

  'isAA',
  'childGender',
  'childBirthDate',
  'childAgeAtDeparture',
  'childSchoolLevel',
  'childNumberOfYearsInLanguage',
  'declaredFriends.id',
  'declaredFriends.firstName',
  'declaredFriends.lastName',
  'declaredFriends.type',
  'declaredFriends.blockedPlaceId'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentAffectedEnrollmentFormType = SelectFields<
  Enrollment,
  typeof enrollmentAffectedEnrollmentFormFields
>

// ------------------------- ENROLLMENT LIST -------------------------
export const enrollmentListFields = [
  'id',
  'uuid',
  'createdAt',
  'enrollmentNumber',
  'childFirstName',
  'childLastName',
  'childBirthDate',
  'status',
  'origin',
  'medicalStatus',
  'medicalAttestationSending.signed',
  'medicalAttestationSending.type',
  'paymentStatus',
  'cseStatus',
  'cseRegistration.cse.identifier',
  'cseRegistration.cse.label',
  'isValidMedicalStatus',
  'medicalLevel',
  'isAgeCompliant',

  'attributedUser.id',
  'attributedUser.firstName',
  'attributedUser.lastName',

  'mainProposition.uuid',
  'mainProposition.options.option.translations',
  'mainProposition.journeyTripPlace.translations',
  'mainProposition.mainTrip.transportMeans.translations',
  'mainProposition.mainTrip.transportMeans.code',
  'mainProposition.mainTrip.departure.translations',

  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.country.name',
  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.period.color',
  'mainProposition.journey.mainOption.translations',

  'propositionType',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.period.color',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.product.country.name',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.option.translations'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentListType = SelectFields<Enrollment, typeof enrollmentListFields>

export const enrollmentValidatedListFields = [
  'id',
  'confirmedAt',
  'paymentStatus',
  'cseStatus',
  'cseRegistration.cse.identifier',
  'cseRegistration.cse.label',
  'enrollmentNumber',
  'childPickupSubmitted',
  'childInfoStatus',
  'childIdentityPapersSubmitted',
  'childHealthPapersSubmitted',
  'clientSpaceReminderByEmailSentAt',
  'clientSpaceReminderByEmailsSent',
  'clientSpaceReminderByPhoneSentAt',
  'clientSpaceReminderByPhoneSent',
  'hasTest',
  'hasSubmittedTest',
  'languageTestSubmission.submittedAt',
  'status',
  'isAA',
  'childFirstName',
  'childLastName',
  'childGender',
  'childBirthDate',
  'childSchoolLevel',
  'medicalLevel',

  'parent1FirstName',
  'parent1LastName',
  'parent1Email',
  'parent2FirstName',
  'parent2LastName',
  'parent2Email',

  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.period.color',

  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.accomodationCategory',
  'mainProposition.journey.date.product.numberOfHoursPerWeek',
  'mainProposition.journey.date.product.type',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.options.option.translations',

  'mainProposition.mainTrip.departure.translations',
  'mainProposition.mainTrip.arrival.translations',
  'mainProposition.mainTrip.journeyPlace.name',
  'mainProposition.mainTrip.transportMeans.translations',
  'mainProposition.mainTrip.hasTransport',

  'mainProposition.secondaryTrip.departure.translations',
  'mainProposition.secondaryTrip.arrival.translations',
  'mainProposition.secondaryTrip.journeyPlace.name',
  'mainProposition.secondaryTrip.transportMeans.translations',

  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.product.country.name',

  'propositionType',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.period.color',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.product.country.name',
  'otoIndividualProposition.option.translations',
  'otoIndividualProposition.numberOfHours',

  'enrollmentCenter.center.name',
  'enrollmentCenter.center.accomodation'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentValidatedListType = SelectFields<
  Enrollment,
  typeof enrollmentValidatedListFields
>

export const enrollmentQuoteListFields = [
  'id',
  'createdAt',
  'childFirstName',
  'childLastName',
  'enrollmentNumber',
  'cseStatus',
  'mainProposition.journey.mainOption.translations',
  'mainProposition.options.option.translations',
  'mainProposition.journey.date.product.formula.id',
  'mainProposition.journey.date.product.formula.translations',
  'mainProposition.journey.date.product.country.name',
  'mainProposition.journey.date.product.country.code',
  'mainProposition.journey.date.departureDate',
  'mainProposition.journey.date.returnDate',
  'mainProposition.journey.date.period.color',

  'propositionType',
  'otoIndividualProposition.departureDate',
  'otoIndividualProposition.returnDate',
  'otoIndividualProposition.period.color',
  'otoIndividualProposition.product.formula.id',
  'otoIndividualProposition.product.formula.translations',
  'otoIndividualProposition.product.country.name',
  'otoIndividualProposition.product.country.code',
  'otoIndividualProposition.option.translations'
] as const satisfies FieldsSelector<Enrollment>

export type EnrollmentQuoteListType = SelectFields<Enrollment, typeof enrollmentQuoteListFields>
